// File: ss-wishlist.js
// Path: assets/ss-wishlist.js
//
// SanSCARi wishlist. Saves hearted products in the shopper's own browser
// (localStorage). No app, no login, no server.
//
// Markup this script looks for (added by the next files):
//   [data-ss-wl-handle="product-handle"]   heart button on cards and product page
//   [data-ss-wl-count]                     header counter
//   [data-ss-wl-page]                      wishlist page wrapper, containing:
//     [data-ss-wl-grid]                    where saved products are drawn
//     [data-ss-wl-empty]                   empty-state block
//     [data-ss-wl-share]                   "copy my wishlist link" button
//
// Public API for later use: window.SSWishlist.get() / has() / add() / remove() / toggle()

(function () {
  'use strict';

  if (window.SSWishlist) return;

  var STORAGE_KEY = 'ss_wishlist_v1';
  var SHARE_PARAM = 'items';
  var MAX_ITEMS = 50;
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Default wording. The wishlist page section can override any of these
  // with a data-text-<key> attribute on the [data-ss-wl-page] element.
  var TEXT = {
    'add-to-cart': 'Add to cart',
    'adding': 'Adding...',
    'added': 'Added to cart.',
    'view-cart': 'View cart',
    'sold-out': 'Sold out',
    'remove': 'Remove from wishlist',
    'options': 'Size',
    'error': 'Could not add this one. Please try again.',
    'load-error': 'Could not load your wishlist. Refresh the page to try again.',
    'copied': 'Link copied',
    'copy-prompt': 'Copy your wishlist link:'
  };

  /* ------------------------------------------------------------------ */
  /* Storage                                                             */
  /* ------------------------------------------------------------------ */

  // Fallback list for browsers that block localStorage (some private modes).
  var memoryList = [];

  function cleanHandle(value) {
    if (typeof value !== 'string') return '';
    var handle = value.trim();
    if (!handle || handle.length > 255) return '';
    if (/[\s\/\\?#&=,"'<>]/.test(handle)) return '';
    return handle;
  }

  function normalise(list) {
    var seen = Object.create(null);
    var out = [];
    for (var i = 0; i < list.length && out.length < MAX_ITEMS; i++) {
      var handle = cleanHandle(list[i]);
      if (handle && !seen[handle]) {
        seen[handle] = true;
        out.push(handle);
      }
    }
    return out;
  }

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      memoryList = normalise(Array.isArray(parsed) ? parsed : []);
    } catch (error) {
      // Storage unavailable: keep using the in-memory list.
    }
    return memoryList.slice();
  }

  function save(list, silent) {
    memoryList = normalise(list);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryList));
    } catch (error) {
      // Storage unavailable: the list still works until the tab closes.
    }
    syncButtons();
    if (!silent) {
      document.dispatchEvent(
        new CustomEvent('ss:wishlist:change', { detail: { handles: memoryList.slice() } })
      );
    }
  }

  function has(handle) {
    return read().indexOf(cleanHandle(handle)) !== -1;
  }

  function add(handle) {
    handle = cleanHandle(handle);
    if (!handle) return false;
    var list = read();
    if (list.indexOf(handle) === -1) {
      list.unshift(handle); // newest first
      save(list);
    }
    return true;
  }

  function remove(handle) {
    handle = cleanHandle(handle);
    var list = read();
    var index = list.indexOf(handle);
    if (index !== -1) {
      list.splice(index, 1);
      save(list);
    }
    return false;
  }

  function toggle(handle) {
    return has(handle) ? remove(handle) : add(handle);
  }

  /* ------------------------------------------------------------------ */
  /* Hearts and header counter                                           */
  /* ------------------------------------------------------------------ */

  function syncButtons() {
    var list = read();
    var i;

    var buttons = document.querySelectorAll('[data-ss-wl-handle]');
    for (i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      var active = list.indexOf(button.getAttribute('data-ss-wl-handle')) !== -1;
      var pressed = active ? 'true' : 'false';
      var label = active
        ? button.getAttribute('data-label-remove') || 'Remove from wishlist'
        : button.getAttribute('data-label-add') || 'Add to wishlist';

      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      button.classList.toggle('is-active', active);
    }

    var counters = document.querySelectorAll('[data-ss-wl-count]');
    var countText = String(list.length);
    for (i = 0; i < counters.length; i++) {
      // Only write when the value changes, so the page observer below never loops.
      if (counters[i].textContent !== countText) counters[i].textContent = countText;
      counters[i].hidden = list.length === 0;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Wishlist page                                                       */
  /* ------------------------------------------------------------------ */

  var productCache = {};
  var renderToken = 0;

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function pageText(page, key) {
    return (page && page.getAttribute('data-text-' + key)) || TEXT[key];
  }

  function productUrl(handle) {
    return ROOT + 'products/' + encodeURIComponent(handle);
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];

    var request = fetch(productUrl(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (response.status === 404) return { status: 'gone' };
        if (!response.ok) return { status: 'error' };
        return response.json().then(function (product) {
          return { status: 'ok', product: product };
        });
      })
      .catch(function () {
        return { status: 'error' };
      });

    productCache[handle] = request;
    request.then(function (result) {
      if (result.status === 'error') delete productCache[handle]; // allow a retry
    });
    return request;
  }

  function formatMoney(cents, format) {
    var amount = Number(cents || 0) / 100;

    function build(decimals, thousands, decimalMark) {
      var parts = amount.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimalMark);
    }

    var template = typeof format === 'string' ? format.replace(/<[^>]*>/g, '') : '';
    if (/\{\{\s*\w+\s*\}\}/.test(template)) {
      return template.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
        switch (key) {
          case 'amount_no_decimals':
            return build(0, ',', '.');
          case 'amount_with_comma_separator':
            return build(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return build(0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return build(2, "'", '.');
          case 'amount_with_space_separator':
            return build(2, ' ', ',');
          case 'amount_no_decimals_with_space_separator':
            return build(0, ' ', ',');
          default:
            return build(2, ',', '.');
        }
      });
    }

    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    try {
      return new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency }).format(amount);
    } catch (error) {
      return '$' + amount.toFixed(2);
    }
  }

  function imageUrl(product, width) {
    var src = typeof product.featured_image === 'string' ? product.featured_image : '';
    if (!src && product.images && typeof product.images[0] === 'string') src = product.images[0];
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function findVariant(product, id) {
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(id)) return product.variants[i];
    }
    return null;
  }

  function writePrice(priceNode, variant, format) {
    priceNode.textContent = '';
    if (!variant) return;
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceNode.appendChild(el('s', 'ss-wl-card__compare', formatMoney(variant.compare_at_price, format)));
      priceNode.appendChild(document.createTextNode(' '));
    }
    priceNode.appendChild(el('span', 'ss-wl-card__amount', formatMoney(variant.price, format)));
  }

  function buildCard(product, page) {
    var format = page.getAttribute('data-money-format');
    var url = productUrl(product.handle);
    var variants = product.variants || [];
    var hasOptions = !(variants.length === 1 && variants[0].title === 'Default Title');
    var firstAvailable = null;
    var i;

    for (i = 0; i < variants.length; i++) {
      if (variants[i].available) {
        firstAvailable = variants[i];
        break;
      }
    }

    var card = el('article', 'ss-wl-card');
    card.setAttribute('data-ss-wl-card', product.handle);

    // Image
    var media = el('a', 'ss-wl-card__media');
    media.href = url;
    var src = imageUrl(product, 600);
    if (src) {
      var img = el('img', 'ss-wl-card__image');
      img.src = src;
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.width = 600;
      img.height = 800;
      media.appendChild(img);
    }
    card.appendChild(media);

    // Remove
    var removeButton = el('button', 'ss-wl-card__remove', '\u00d7');
    removeButton.type = 'button';
    removeButton.setAttribute('data-ss-wl-remove', product.handle);
    removeButton.setAttribute('aria-label', pageText(page, 'remove'));
    card.appendChild(removeButton);

    // Title and price
    var title = el('a', 'ss-wl-card__title', product.title || '');
    title.href = url;
    card.appendChild(title);

    var price = el('div', 'ss-wl-card__price');
    writePrice(price, firstAvailable || variants[0], format);
    card.appendChild(price);

    // Size picker and add to cart
    var actions = el('div', 'ss-wl-card__actions');
    var atc = el('button', 'ss-wl-card__atc button', pageText(page, 'add-to-cart'));
    atc.type = 'button';
    atc.setAttribute('data-ss-wl-atc', '');

    if (!firstAvailable) {
      atc.disabled = true;
      atc.textContent = pageText(page, 'sold-out');
    } else if (hasOptions) {
      var select = el('select', 'ss-wl-card__select');
      var optionNames = (product.options || [])
        .map(function (option) {
          return option && option.name ? option.name : '';
        })
        .filter(Boolean)
        .join(' / ');
      select.setAttribute('aria-label', optionNames || pageText(page, 'options'));

      for (i = 0; i < variants.length; i++) {
        var option = el('option', '', variants[i].public_title || variants[i].title);
        option.value = String(variants[i].id);
        if (!variants[i].available) {
          option.disabled = true;
          option.textContent += ' - ' + pageText(page, 'sold-out');
        }
        if (variants[i] === firstAvailable) option.selected = true;
        select.appendChild(option);
      }

      select.addEventListener('change', function () {
        writePrice(price, findVariant(product, select.value), format);
      });
      actions.appendChild(select);
    } else {
      atc.setAttribute('data-variant-id', String(firstAvailable.id));
    }

    actions.appendChild(atc);
    card.appendChild(actions);

    var message = el('p', 'ss-wl-card__msg');
    message.setAttribute('role', 'status');
    message.hidden = true;
    card.appendChild(message);

    return card;
  }

  function setPageState(page, count) {
    var grid = page.querySelector('[data-ss-wl-grid]');
    var empty = page.querySelector('[data-ss-wl-empty]');
    var share = page.querySelector('[data-ss-wl-share]');
    if (grid) grid.hidden = count === 0;
    if (empty) empty.hidden = count > 0;
    if (share) share.hidden = count === 0;
  }

  function renderPage() {
    var page = document.querySelector('[data-ss-wl-page]');
    if (!page) return;
    var grid = page.querySelector('[data-ss-wl-grid]');
    if (!grid) return;

    var list = read();
    var token = ++renderToken;

    if (!list.length) {
      grid.textContent = '';
      setPageState(page, 0);
      return;
    }

    page.setAttribute('aria-busy', 'true');

    Promise.all(list.map(fetchProduct)).then(function (results) {
      if (token !== renderToken) return; // a newer render started

      var products = [];
      var gone = [];
      results.forEach(function (result, index) {
        if (result.status === 'ok') products.push(result.product);
        if (result.status === 'gone') gone.push(list[index]);
      });

      // Quietly forget products that no longer exist in the store.
      if (gone.length) {
        save(
          read().filter(function (handle) {
            return gone.indexOf(handle) === -1;
          }),
          true
        );
      }

      grid.textContent = '';
      products.forEach(function (product) {
        grid.appendChild(buildCard(product, page));
      });

      var remaining = read().length;
      setPageState(page, remaining);
      if (remaining > 0 && !products.length) {
        grid.appendChild(el('p', 'ss-wl-page__error', pageText(page, 'load-error')));
      }

      page.removeAttribute('aria-busy');
      syncButtons();
    });
  }

  // Keeps the page in step with the saved list without redrawing everything.
  function syncPage() {
    var page = document.querySelector('[data-ss-wl-page]');
    if (!page) return;
    var grid = page.querySelector('[data-ss-wl-grid]');
    if (!grid) return;

    var list = read();
    var cards = grid.querySelectorAll('[data-ss-wl-card]');
    var shown = [];

    for (var i = 0; i < cards.length; i++) {
      var handle = cards[i].getAttribute('data-ss-wl-card');
      if (list.indexOf(handle) === -1) {
        cards[i].parentNode.removeChild(cards[i]);
      } else {
        shown.push(handle);
      }
    }

    var missing = list.some(function (handle) {
      return shown.indexOf(handle) === -1;
    });

    if (missing) {
      renderPage();
    } else {
      setPageState(page, list.length);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Add to cart from the wishlist page                                  */
  /* ------------------------------------------------------------------ */

  function showMessage(card, page, text, withCartLink) {
    var message = card.querySelector('.ss-wl-card__msg');
    if (!message) return;
    message.textContent = text;
    if (withCartLink) {
      message.appendChild(document.createTextNode(' '));
      var link = el('a', 'ss-wl-card__cart-link', pageText(page, 'view-cart'));
      link.href = ROOT + 'cart';
      message.appendChild(link);
    }
    message.hidden = false;
  }

  // Best effort: refresh the header cart count the way Dawn-based themes do.
  // Does nothing if the theme's cart bubble is not found.
  function refreshCartCount() {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    fetch(ROOT + '?section_id=cart-icon-bubble')
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (html) {
        if (!html) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  }

  function addToCart(button) {
    var card = button.closest('[data-ss-wl-card]');
    var page = button.closest('[data-ss-wl-page]');
    if (!card || button.disabled) return;

    var select = card.querySelector('.ss-wl-card__select');
    var variantId = select ? select.value : button.getAttribute('data-variant-id');
    if (!variantId) return;

    var label = button.textContent;
    button.disabled = true;
    button.textContent = pageText(page, 'adding');

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            return { ok: response.ok, data: data };
          });
      })
      .then(function (result) {
        if (!result.ok) {
          throw new Error(result.data.description || result.data.message || pageText(page, 'error'));
        }
        showMessage(card, page, pageText(page, 'added'), true);
        refreshCartCount();
      })
      .catch(function (error) {
        showMessage(card, page, (error && error.message) || pageText(page, 'error'), false);
      })
      .then(function () {
        button.disabled = false;
        button.textContent = label;
      });
  }

  /* ------------------------------------------------------------------ */
  /* Share link                                                          */
  /* ------------------------------------------------------------------ */

  function legacyCopy(text) {
    return new Promise(function (resolve, reject) {
      var field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      var copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }
      document.body.removeChild(field);
      if (copied) {
        resolve();
      } else {
        reject(new Error('copy failed'));
      }
    });
  }

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(function () {
        return legacyCopy(text);
      });
    }
    return legacyCopy(text);
  }

  function shareLink(button) {
    var list = read();
    if (!list.length) return;

    var page = button.closest('[data-ss-wl-page]');
    var url =
      window.location.origin +
      window.location.pathname +
      '?' +
      SHARE_PARAM +
      '=' +
      list.map(encodeURIComponent).join(',');

    copyText(url).then(
      function () {
        if (!button.hasAttribute('data-original-text')) {
          button.setAttribute('data-original-text', button.textContent);
        }
        button.textContent = pageText(page, 'copied');
        window.setTimeout(function () {
          button.textContent = button.getAttribute('data-original-text');
        }, 2000);
      },
      function () {
        window.prompt(pageText(page, 'copy-prompt'), url);
      }
    );
  }

  // Opening a shared link adds those products to this browser's wishlist.
  function importShared() {
    if (!document.querySelector('[data-ss-wl-page]')) return;

    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return;
    }

    var raw = params.get(SHARE_PARAM);
    if (!raw) return;

    var incoming = normalise(raw.split(','));
    if (incoming.length) save(read().concat(incoming), true);

    params.delete(SHARE_PARAM);
    var query = params.toString();
    try {
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (query ? '?' + query : '') + window.location.hash
      );
    } catch (error) {
      // Leaving the parameter in the address bar is harmless.
    }
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                              */
  /* ------------------------------------------------------------------ */

  // Capture phase, so a heart inside a product link never opens the product.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var heart = target.closest('[data-ss-wl-handle]');
      if (heart) {
        event.preventDefault();
        event.stopPropagation();
        toggle(heart.getAttribute('data-ss-wl-handle'));
        heart.classList.remove('ss-wl-pop');
        void heart.offsetWidth; // restart the pop animation
        heart.classList.add('ss-wl-pop');
        return;
      }

      var removeButton = target.closest('[data-ss-wl-remove]');
      if (removeButton) {
        event.preventDefault();
        remove(removeButton.getAttribute('data-ss-wl-remove'));
        return;
      }

      var atc = target.closest('[data-ss-wl-atc]');
      if (atc) {
        event.preventDefault();
        addToCart(atc);
        return;
      }

      var share = target.closest('[data-ss-wl-share]');
      if (share) {
        event.preventDefault();
        shareLink(share);
      }
    },
    true
  );

  document.addEventListener('ss:wishlist:change', syncPage);

  // Another tab changed the wishlist.
  window.addEventListener('storage', function (event) {
    if (event.key === STORAGE_KEY) {
      syncButtons();
      syncPage();
    }
  });

  // Coming back with the browser's back button.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      syncButtons();
      syncPage();
    }
  });

  function init() {
    importShared();
    syncButtons();
    renderPage();

    // Product cards that load later (load more, quick view, recommendations)
    // get their heart state as soon as they appear.
    if ('MutationObserver' in window && document.body) {
      var timer;
      new MutationObserver(function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(syncButtons, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.SSWishlist = {
    get: read,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    refresh: function () {
      syncButtons();
      syncPage();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();